
import Vue, { PropType } from 'vue';
import ReferenceService from '@/services/ReferenceService';

type TemplateField = {
  name: string;
  type:
    | 'string'
    | 'number'
    | 'year'
    | 'date'
    | 'selection'
    | 'boolean'
    | 'reference'
    | string;
  label?: string;
  required?: boolean;
  items?: any[];
};

type ReferenceTemplate = { uuid: string; structure?: TemplateField[] };
type ReferenceRow = { uuid: string; payload?: Record<string, any> | null };

export default Vue.extend({
  name: 'DeviceReferenceEditDialog',
  props: {
    mode: { type: String as PropType<'create' | 'edit'>, default: 'create' },
    device: { type: Object as PropType<{ uuid: string }>, required: true },
    reference_data: { type: Array as PropType<ReferenceRow[] | null>, default: [] },
    template_id: { type: String },
    property_id: { type: String },
  },
  data: (vm: any): any => ({
    localdialog: false,
    formIsValid: true,
    isSaving: false,
    alertText: '',
    alertType: 'error',
    selectedReferenceData: {} as Object as PropType<ReferenceRow>,
    templates: [] as any[],
    template: {} as Object as PropType<ReferenceTemplate>,
    referenceFormPayload: {} as Record<string, any>,
  }),
  async created() {

    const all = await ReferenceService.getTemplateCollection();
    this.templates = all.filter(
      (t: any) => t.entityType === 'devices'
    );
  },
  methods: {
    getFieldLabel(field: TemplateField) {
      return field.label || field.name;
    },
    getValidationRulesForField(field: TemplateField) {
      if (!field.required) return [];
      return [
        (v: any) => {
          if (v === undefined) return this.requiredError;
          if (
            v === '' &&
            Array.isArray(field.items) &&
            field.items.includes('')
          ) {
            return true;
          }
          return !!v || v === 0 || v === false || this.requiredError;
        },
      ];
    },
    initializePayloadFromTemplateAndExistingData() {
      const initial: Record<string, any> =
        this.mode === 'edit' && this.selectedReferenceData?.payload
          ? { ...(this.selectedReferenceData!.payload as Record<string, any>) }
          : {};

      const allFieldNames = this.templateFields.map(
        (f: TemplateField) => f.name
      );

      // We set all boolean fields explicitly to false
      // Otherwise the v-switch will show an error on save as its undefined
      for(const boolField of this.templateFields.filter((f: TemplateField) => f.type === 'boolean')) {
        initial[boolField.name] = false;
      }

      if (allFieldNames.includes('bascloud_device_id')) {
        initial['bascloud_device_id'] = this.device.uuid;
      }
      if (allFieldNames.includes('bascloud_property_id') && this.property_id) {
        initial['bascloud_property_id'] = this.property_id;
      }

      this.referenceFormPayload = initial;
    },
    async saveReferenceData() {
      const formRef = this.$refs.form as any;
      if (formRef && !formRef.validate()) return;

      this.isSaving = true;
      this.alertText = '';

      try {
        if (this.mode === 'edit' && this.selectedReferenceData) {
          const updated_reference = await ReferenceService.updateData(
            this.currentTenant,
            this.template.uuid,
            this.selectedReferenceData.uuid,
            this.referenceFormPayload
          );
          this.$emit('update', updated_reference);
        } else {
          // Entity id should be the same for all reference data entries of this entity, get the first non null
          const entityId = this.reference_data.find((obj: { uuid: any; }) => obj.uuid)?.uuid;

          let created_reference;
          if(entityId) {
            created_reference = await ReferenceService.addData(
              this.currentTenant,
              this.template.uuid,
              entityId,
              'devices',
              this.referenceFormPayload
            );
          } else {
            created_reference = await ReferenceService.createData(
              this.currentTenant,
              this.template.uuid,
              'devices',
              this.referenceFormPayload
            );
          }
          this.$emit('create', created_reference);
        }

        this.alertType = 'success';
        this.alertText = this.$t(
          this.mode === 'edit' ? 'device.successUpdate' : 'device.successCreate'
        ) as string;
        this.localdialog = false;
      } catch (err: any) {
        console.error(err);
        const errs = err?.response?.data?.errors;
        const msg =
          Array.isArray(errs) && errs.length
            ? errs[0]?.detail
            : err?.message || 'Error';
        this.alertType = 'error';
        this.alertText = msg;
      } finally {
        this.isSaving = false;
        this.alertText = '';
      }
    },
    dismiss() {
      this.clear();
      this.close();
    },
    clear() {
      this.alertText = '';
      this.alertType = 'error' 
      this.template = {};
      this.referenceFormPayload = {};
    },
    close() {
      this.localdialog = false;
    },
  },
  watch: {
    localdialog(newValue: boolean) {
      if (!newValue) {
        this.$emit('close');
      } else {
        
        this.selectedReferenceData = this.reference_data.find((r: any) => r.templateId === this.template_id);

        if (this.mode === 'edit' && this.reference_data.length > 0) {
          this.template =
            this.templates.find(
              (t: any) => t.uuid === this.selectedReferenceData?.templateId
            ) || this.templates[0];
          this.initializePayloadFromTemplateAndExistingData();
        } else if(this.mode === 'create') {
          // Filter out all templates that already have data
          const existing_templates = this.reference_data.map((r: any) => r.templateId);
          this.templates = this.templates.filter((t: any) => !existing_templates.includes(t.uuid));

          if(this.templates.length > 0) {
            this.template = this.templates[0];
          }      
          this.initializePayloadFromTemplateAndExistingData();
        }
      }
    },
    template() {
      this.selectedReferenceData = this.reference_data.find((r: any) => r.templateId === this.template.uuid);
      this.initializePayloadFromTemplateAndExistingData();
    }
  },
  computed: {
    currentTenant(): any {
      return this.$root.$store.state.session.selectedTenant.uuid;
    },
    requiredError(): any {
      return this.$t('required');
    },
    templateFields(): TemplateField[] {
      const def = this.template;
      return Array.isArray(def?.structure)
        ? (def.structure as TemplateField[])
        : [];
    },
    templateFieldsForRendering(): TemplateField[] {
      return this.templateFields.filter((field: TemplateField) => {
        // For now, we dont show reference (implicit value) and groups (todo)
        return (field.type !== 'reference' && field.type !== 'group')
      });
    },
  },
});
